import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';

/**
 * Route-driven sheets (D-48): a detail, create, or categories URL renders its list with the sheet
 * open. A sheet opened from its list carries this state, so closing it steps back in history (the
 * back button and the close button agree); a deep-linked sheet closes to its list instead.
 */
export const OPENED_FROM_LIST = { openedFromList: true } as const;

/**
 * The close handler of a route sheet over `listPath`; the list keeps its filters (the query).
 * `pageParams` are the detail page's own query keys (its `tab`), which the list must not inherit
 * (W4: «بازگشت به فهرست» landed on `/merchants?tab=people`).
 */
export function useCloseSheet(listPath: string, pageParams: readonly string[] = []): () => void {
  const navigate = useNavigate();
  const location = useLocation();
  const fromList =
    (location.state as { readonly openedFromList?: boolean } | null)?.openedFromList === true;
  const pageKeys = pageParams.join(',');
  return useCallback(() => {
    if (fromList) {
      void navigate(-1);
    } else {
      const search = new URLSearchParams(location.search);
      for (const key of pageKeys === '' ? [] : pageKeys.split(',')) search.delete(key);
      const query = search.toString();
      void navigate(
        { pathname: listPath, search: query === '' ? '' : `?${query}` },
        { replace: true },
      );
    }
  }, [fromList, listPath, location.search, navigate, pageKeys]);
}

/** `onOpenChange` for a sheet that is open while its route matches. */
export function onSheetOpenChange(close: () => void): (open: boolean) => void {
  return (open) => {
    if (!open) {
      close();
    }
  };
}
